import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  MessageSquare, Send, Mail, MailOpen, Inbox, User, Reply, CornerDownRight, Image, X, Upload, Camera
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import API from '@/config/api';
// API imported from config;

const MessagesPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    recipient_id: '',
    subject: '',
    content: '',
    reply_to: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch messages first - this should always work
      const messagesRes = await axios.get(`${API}/messages`, getAuthHeaders());
      setMessages(messagesRes.data);
      
      // Fetch users for recipient selection based on role
      try {
        if (user.role === 'parent' || user.role === 'driver') {
          // Parents use dedicated endpoint to get admins they can message
          const adminsRes = await axios.get(`${API}/users/admins/list`, getAuthHeaders());
          setUsers(adminsRes.data.filter(u => u.id !== user.id));
        } else {
          // Admins/staff can see all users
          const usersRes = await axios.get(`${API}/users`, getAuthHeaders());
          setUsers(usersRes.data.filter(u => u.id !== user.id));
        }
      } catch (usersError) {
        // Fallback: extract admin info from received messages
        const adminSenders = messagesRes.data
          .filter(m => m.sender_id !== user.id)
          .reduce((acc, m) => {
            if (!acc.find(u => u.id === m.sender_id)) {
              acc.push({ id: m.sender_id, name: m.sender_name, role: 'admin' });
            }
            return acc;
          }, []);
        setUsers(adminSenders);
      }
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/messages`, {
        ...formData,
        attachments: attachments.map(a => a.url)
      }, getAuthHeaders());
      toast.success(formData.reply_to ? 'Reply sent successfully' : 'Message sent successfully');
      setDialogOpen(false);
      setFormData({ recipient_id: '', subject: '', content: '', reply_to: null });
      setAttachments([]);
      setSelectedMessage(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingPhoto(true);
    
    for (const file of files) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        const response = await axios.post(`${API}/messages/upload-photo`, formDataUpload, {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        setAttachments(prev => [...prev, {
          url: response.data.url,
          filename: response.data.filename,
          preview: URL.createObjectURL(file)
        }]);
        
        toast.success('Photo uploaded');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload photo');
      }
    }
    
    setUploadingPhoto(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleReply = (message) => {
    // Pre-fill the form for reply
    setFormData({
      recipient_id: message.sender_id,
      subject: message.subject.startsWith('Re: ') ? message.subject : `Re: ${message.subject}`,
      content: '',
      reply_to: message.id
    });
    setSelectedMessage(null);
    setDialogOpen(true);
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, getAuthHeaders());
      fetchData();
    } catch (error) {
      // Silently fail
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_read && message.recipient_id === user.id) {
      markAsRead(message.id);
    }
  };

  const inbox = messages.filter(m => m.recipient_id === user.id);
  const sent = messages.filter(m => m.sender_id === user.id);
  const unreadCount = inbox.filter(m => !m.is_read).length;

  if (loading) {
    return (
      <DashboardLayout title="Messages">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Messages">
      <div className="space-y-6" data-testid="messages-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted-foreground">
              {unreadCount > 0 ? `You have ${unreadCount} unread message(s)` : 'All caught up!'}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setFormData({ recipient_id: '', subject: '', content: '', reply_to: null });
              setAttachments([]);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="compose-btn">
                <Send className="w-4 h-4 mr-2" />
                Compose
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {formData.reply_to ? 'Reply to Message' : 'New Message'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSendMessage} className="space-y-4 mt-4">
                {formData.reply_to && (
                  <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3 rounded-lg flex items-center gap-2">
                    <Reply className="w-4 h-4" />
                    Replying to: {formData.subject.replace('Re: ', '')}
                  </div>
                )}
                {user.role === 'parent' && !formData.reply_to && (
                  <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3 rounded-lg">
                    As a parent, you can send messages directly to the center director/administrator.
                  </div>
                )}
                <div className="space-y-2">
                  <Label>To</Label>
                  <Select 
                    value={formData.recipient_id} 
                    onValueChange={(value) => setFormData({...formData, recipient_id: value})}
                    disabled={!!formData.reply_to}
                  >
                    <SelectTrigger data-testid="message-recipient-select">
                      <SelectValue placeholder={user.role === 'parent' ? "Select administrator" : "Select recipient"} />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} {user.role === 'parent' ? '(Director)' : `(${u.role})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    placeholder="Message subject"
                    required
                    readOnly={!!formData.reply_to}
                    className={formData.reply_to ? 'bg-muted' : ''}
                    data-testid="message-subject-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder={formData.reply_to ? "Type your reply here..." : "Type your message here..."}
                    rows={5}
                    required
                    data-testid="message-content-input"
                  />
                </div>

                {/* Photo Upload Section */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Attach Photos (Optional)
                  </Label>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                  
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                  >
                    {uploadingPhoto ? (
                      <>Uploading...</>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Add Photos
                      </>
                    )}
                  </Button>
                  
                  {/* Photo Previews */}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {attachments.map((attachment, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={attachment.preview}
                            alt={`Attachment ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removeAttachment(idx)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    setFormData({ recipient_id: '', subject: '', content: '', reply_to: null });
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="send-message-btn">
                    {formData.reply_to ? (
                      <>
                        <Reply className="w-4 h-4 mr-2" />
                        Send Reply
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Message View Dialog */}
        <Dialog open={!!selectedMessage} onOpenChange={(open) => !open && setSelectedMessage(null)}>
          <DialogContent className="max-w-2xl">
            {selectedMessage && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading">{selectedMessage.subject}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      From: <strong>{selectedMessage.sender_name}</strong>
                    </span>
                    <span>
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="p-4 bg-muted rounded-xl whitespace-pre-wrap">
                    {selectedMessage.content}
                  </div>
                  
                  {/* Display Photo Attachments */}
                  {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Image className="w-4 h-4" />
                        Photos ({selectedMessage.attachments.length})
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {selectedMessage.attachments.map((url, idx) => (
                          <a 
                            key={idx} 
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={url}
                              alt={`Attachment ${idx + 1}`}
                              className="w-full h-40 object-cover rounded-lg border hover:opacity-90 transition-opacity cursor-pointer"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Reply Button - Show only for received messages */}
                  {selectedMessage.sender_id !== user.id && (
                    <div className="flex justify-end pt-2">
                      <Button 
                        onClick={() => handleReply(selectedMessage)}
                        data-testid="reply-btn"
                      >
                        <Reply className="w-4 h-4 mr-2" />
                        Reply
                      </Button>
                    </div>
                  )}
                  
                  {/* Show if this is a reply */}
                  {selectedMessage.reply_to && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3">
                      <CornerDownRight className="w-3 h-3" />
                      <span>This is a reply to a previous message</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Inbox */}
          <Card className="card-clay">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Inbox className="w-5 h-5 text-primary" />
                Inbox
                {unreadCount > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {inbox.length > 0 ? (
                <div className="space-y-2">
                  {inbox.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "p-4 rounded-xl cursor-pointer transition-colors",
                        message.is_read 
                          ? "bg-muted/50 hover:bg-muted" 
                          : "bg-primary/5 border border-primary/20 hover:bg-primary/10"
                      )}
                      onClick={() => openMessage(message)}
                      data-testid={`inbox-message-${message.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                          {message.is_read ? (
                            <MailOpen className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <Mail className="w-5 h-5 text-primary" />
                          )}
                          <div>
                            <p className={cn("font-medium flex items-center gap-2", !message.is_read && "font-semibold")}>
                              {message.subject}
                              {message.attachments && message.attachments.length > 0 && (
                                <Image className="w-4 h-4 text-blue-500" />
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              From: {message.sender_name}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Inbox className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Your inbox is empty</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sent */}
          <Card className="card-clay">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sent.length > 0 ? (
                <div className="space-y-2">
                  {sent.map((message) => (
                    <div
                      key={message.id}
                      className="p-4 rounded-xl bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => openMessage(message)}
                      data-testid={`sent-message-${message.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <User className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{message.subject}</p>
                            <p className="text-sm text-muted-foreground">
                              To: {message.recipient_name}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Send className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No sent messages</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MessagesPage;
